import React, { useCallback, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import GradedCanvas from './GradedCanvas';
import { fmtTime } from '../utils/movie';

const COUNT_OPTIONS = [4, 6, 9, 12];
const THUMB_W = 960; // captured frame width (px); height follows source AR

// Grade-aware contact sheet for ONE clip. It scrubs the clip's preview proxy
// ONCE, grabbing N evenly-spaced frames into small offscreen canvases, then
// renders each through the SAME 3D-LUT grade pipeline as the main preview
// (GradedCanvas). Because the grade lives in a GPU LUT, dragging any color/
// light slider re-renders all thumbnails instantly — you judge the look across
// the clip's full lighting range (barrel shadow → bright spray → backlight)
// without ever rendering the clip to video.
//
// Props:
//   url         preview source (RED HLS proxy .m3u8, or a presigned/CDN MP4)
//   srcOff      source-second of the file's t=0 (SDK preview proxies are
//               clip-window files; all times here stay in SOURCE seconds)
//   streamType  'hls' to force hls.js (else inferred from the url extension)
//   inSec       clip in-point (source seconds)
//   outSec      clip out-point (source seconds; 0 → use full duration)
//   speed       clip speed (to map a thumb's source time → movie time)
//   startInMovie movie-time where this clip begins (for onSeek)
//   clipKey     identity of the active clip — re-captures when it changes
//   grade       the grade to preview (same object the main right pane uses)
//   baseGrade   optional grade applied BEFORE `grade` (clip ↔ movie stack)
//   onSeek      (movieSec) => void — clicking a thumb jumps the main preview
//   onFrames    (sourceTimes[]) => void — fires once per capture with the
//               sampled source timestamps (lets the studio prefetch hi-res
//               frames for the same spots while the playhead is parked)
export default function ClipFilmstrip({
  url,
  srcOff = 0,
  streamType,
  inSec = 0,
  outSec = 0,
  speed = 1,
  startInMovie = 0,
  clipKey,
  grade,
  baseGrade,
  onSeek,
  onFrames,
}) {
  const [count, setCount] = useState(6);
  const [frames, setFrames] = useState([]); // [{ t, canvas, rawUrl }]
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [progress, setProgress] = useState(0);
  const [nonce, setNonce] = useState(0); // manual re-capture trigger
  const [compare, setCompare] = useState(false); // split raw|graded per thumb
  // Latest callback without retriggering the capture effect.
  const onFramesRef = useRef(onFrames);
  onFramesRef.current = onFrames;

  // Capture N frames from the clip's proxy, then tear the hidden video down so
  // only the lightweight thumbnails (canvases) remain — no lingering decoders.
  useEffect(() => {
    if (!url) { setFrames([]); setStatus('idle'); return undefined; }
    let cancelled = false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    let hls = null;

    setStatus('loading');
    setProgress(0);
    setFrames([]);

    const cleanup = () => {
      try { if (hls) hls.destroy(); } catch { /* noop */ }
      try { video.removeAttribute('src'); video.load(); } catch { /* noop */ }
    };

    const waitSeek = (t) => new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        video.removeEventListener('seeked', finish);
        // One extra rAF so the decoded frame is actually presentable.
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      };
      video.addEventListener('seeked', finish);
      try { video.currentTime = Math.max(0, t - (srcOff || 0)); } catch { finish(); }
      // Safety net if 'seeked' never fires (e.g. clamped to same time).
      setTimeout(finish, 1500);
    });

    const grab = (t) => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) return null;
      const cw = Math.min(THUMB_W, w);
      const ch = Math.max(1, Math.round((cw * h) / w));
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      try {
        ctx.drawImage(video, 0, 0, cw, ch);
      } catch {
        return null; // cross-origin taint — skip this frame
      }
      return canvas;
    };

    const run = async () => {
      const dur = Number.isFinite(video.duration) ? video.duration : 0;
      const lo = Math.max(0, inSec || 0);
      const hi = outSec && outSec > 0 ? outSec : (dur ? dur + (srcOff || 0) : lo + 1);
      const span = Math.max(0.001, hi - lo);
      const out = [];
      for (let i = 0; i < count; i += 1) {
        if (cancelled) return;
        // Sample the middle of each of `count` equal buckets across the clip.
        const t = lo + span * ((i + 0.5) / count);
        // eslint-disable-next-line no-await-in-loop
        await waitSeek(Math.min(t, hi - 0.001));
        if (cancelled) return;
        const canvas = grab(t);
        if (canvas) {
          // A baked JPEG of the raw frame for the side-by-side compare layer
          // (cheaper than a second WebGL context per thumbnail).
          let rawUrl = null;
          try { rawUrl = canvas.toDataURL('image/jpeg', 0.85); } catch { rawUrl = null; }
          out.push({ t, canvas, rawUrl });
          // Show thumbnails as they come in for a responsive feel.
          setFrames(out.slice());
        }
        setProgress((i + 1) / count);
      }
      if (!cancelled) {
        setStatus(out.length ? 'ready' : 'error');
        cleanup();
        if (out.length && onFramesRef.current) onFramesRef.current(out.map((f) => f.t));
      }
    };

    const onMeta = () => { run().catch(() => { if (!cancelled) setStatus('error'); }); };
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('error', () => { if (!cancelled) setStatus('error'); });

    const isHls = streamType === 'hls' || /\.m3u8(\?|$)/.test(url);
    if (isHls && !video.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data?.fatal && !cancelled) setStatus('error');
      });
    } else {
      video.src = url;
      video.load();
    }

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onMeta);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, srcOff, streamType, inSec, outSec, clipKey, count, nonce]);

  const recapture = useCallback(() => setNonce((n) => n + 1), []);

  const thumbSeek = (t) => {
    if (!onSeek) return;
    onSeek(startInMovie + Math.max(0, t - (inSec || 0)) / (speed || 1));
  };

  return (
    <div className="cs-filmstrip">
      <div className="cs-filmstrip-bar">
        <span className="cs-filmstrip-title">Frame strip</span>
        <span className="cs-help" style={{ flex: '1 1 auto', fontSize: 12 }}>
          Your live grade applied across the whole clip — no render. Click a frame to jump there.
        </span>
        <label className="cs-help" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          Frames
          <select
            className="cs-input cs-input-sm"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            style={{ width: 64 }}
          >
            {COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          className={`cs-btn cs-btn-sm ${compare ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
          onClick={() => setCompare((c) => !c)}
          title="Split each frame: raw on the left, your graded look on the right"
        >
          ⇄ Compare raw
        </button>
        <button
          className="cs-btn cs-btn-ghost cs-btn-sm"
          onClick={recapture}
          disabled={status === 'loading' || !url}
          title="Re-grab the sample frames (e.g. after trimming the clip)"
        >
          ↻ Refresh
        </button>
      </div>

      {status === 'error' && (
        <div className="cs-empty" style={{ fontSize: 13 }}>
          Couldn’t sample frames from this clip’s preview. The look still bakes into the export.
        </div>
      )}

      {(status === 'loading' || frames.length > 0) && (
        <div className="cs-filmstrip-grid">
          {frames.map((f, i) => (
            <button
              key={`${clipKey}-${i}-${f.t.toFixed(3)}`}
              type="button"
              className={`cs-filmstrip-cell${compare && f.rawUrl ? ' is-compare' : ''}`}
              onClick={() => thumbSeek(f.t)}
              title={`Jump to ${fmtTime((startInMovie || 0) + Math.max(0, f.t - (inSec || 0)) / (speed || 1))}`}
            >
              {compare && f.rawUrl && (
                <img className="cs-filmstrip-raw" src={f.rawUrl} alt="" draggable={false} />
              )}
              <GradedCanvas
                source={f.canvas}
                grade={grade}
                baseGrade={baseGrade}
                maxWidth={THUMB_W}
                className={compare && f.rawUrl ? 'cs-filmstrip-graded' : undefined}
                style={
                  compare && f.rawUrl
                    ? undefined
                    : { width: '100%', height: 'auto', display: 'block' }
                }
              />
              {compare && f.rawUrl && (
                <>
                  <span className="cs-filmstrip-div" />
                  <span className="cs-filmstrip-tag cs-filmstrip-tag-l">RAW</span>
                  <span className="cs-filmstrip-tag cs-filmstrip-tag-r">GRADED</span>
                </>
              )}
              <span className="cs-filmstrip-tc">{fmtTime(f.t)}</span>
            </button>
          ))}
          {status === 'loading' && Array.from({ length: Math.max(0, count - frames.length) }).map((_, i) => (
            <div key={`ph-${i}`} className="cs-filmstrip-cell cs-filmstrip-ph">
              <span className="cs-spinner" />
            </div>
          ))}
        </div>
      )}

      {status === 'loading' && (
        <div className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>
          Sampling frames… {Math.round(progress * 100)}%
        </div>
      )}
    </div>
  );
}
